import { AlertTriangle } from "lucide-react";
import { Notify } from "@/components/ui/notify";
import type { ExternalInterference } from "@/types/applied-scenario.types";

interface ExternalInterferenceListProps {
  /** `null` = esta leitura não verificou (é o caso da lista). Não é o mesmo que "não houve". */
  interference: ExternalInterference | null;
}

// A INTERFERÊNCIA EXTERNA: ordens que produziram na linha dentro
// da vigência e não estavam no escopo comprometido.
// Ela aparece com o MESMO peso visual do número da apuração, e não como nota de rodapé: um desvio
// causado por uma ordem que ninguém comprometeu não é erro do gêmeo, e sem esta lista a conclusão
// natural de quem lê é "a simulação errou".
// E "não houve interferência" é DECLARADO, não omitido: ausência de aviso não pode ser
// confundida com ausência de verificação.

export function ExternalInterferenceList({ interference }: ExternalInterferenceListProps) {
  if (!interference) return null;

  if (!interference.detected) {
    return (
      <Notify.Banner
        variant="success"
        title="Nenhuma interferência externa na vigência"
        description="Toda a produção da linha nesta janela veio das ordens comprometidas — o desvio apurado é do cenário, não de outra ordem."
      />
    );
  }

  return (
    <div className="space-y-2">
      <h3 className="flex items-center gap-1.5 text-[13px] font-medium">
        <AlertTriangle className="h-4 w-4 text-rd-warning-500" aria-hidden="true" />
        Interferência externa na vigência
      </h3>
      <p className="text-[12px] text-text-secondary">
        Estas ordens produziram na linha durante a vigência e{" "}
        <strong className="font-medium text-text-primary">
          não estavam no escopo comprometido
        </strong>
        . Elas consumiram capacidade que o cenário contava ter — parte do desvio abaixo é delas, não
        do gêmeo.
      </p>
      <ul className="border-rd-warning-500/30 divide-y divide-border-subtle rounded-md border">
        {interference.orders.map((order) => (
          <li
            key={order.productionOrderId}
            className="flex items-center justify-between gap-3 px-3 py-2 text-sm"
          >
            <span className="font-medium">{order.code ?? "Ordem sem código"}</span>
            <span className="tabular-nums text-text-secondary">
              {order.producedQty.toLocaleString("pt-BR")} un produzidas
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
